/**
 * Shared UI primitives — `Header`, `Modal`, `Empty` are ports of the original components
 * (L422-438) with identical markup; the rest are small additions (loading / error states,
 * pager, filter chips).
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cn } from '@pirochi/shared';

export function Header({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-7 flex flex-col justify-between gap-3 sm:flex-row sm:items-end no-print">
      <div>
        <div className="mb-1.5 flex items-center gap-2 text-[11px] font-bold text-[#76aa8a]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#8B2942]"></span>PIROCHI / Chocolatier
        </div>
        <h1 className="text-2xl font-extrabold text-[#5C1528] sm:text-[1.7rem]">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[#789184]">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = box.current?.querySelector<HTMLElement>('input,select,textarea,button:not([data-close])');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="modal-bg" onClick={onClose}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        className={'rounded-2xl bg-white p-5 sm:p-6 shadow-2xl max-h-[92vh] overflow-y-auto ' + (wide ? 'w-full max-w-3xl' : 'w-full max-w-lg')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 id={id} className="text-lg font-extrabold text-[#5C1528]">
            {title}
          </h3>
          <button type="button" data-close onClick={onClose} aria-label="إغلاق" className="text-[#a84d62] text-2xl leading-none px-1">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Empty({ t }: { t?: string }) {
  return <div className="empty card">{t || 'لا توجد بيانات — أضف سجلاً جديداً'}</div>;
}

export function Loading({ label = 'جاري التحميل…' }: { label?: string }) {
  return (
    <div className="empty card" role="status" aria-busy="true">
      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#e0c8ce] border-t-[#8B2942] align-middle ml-2"></span>
      {label}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="card p-6 text-center text-sm text-rose-700" role="alert">
      <div className="font-bold mb-2">تعذر تحميل البيانات</div>
      <div className="mb-3">{error instanceof Error ? error.message : String(error)}</div>
      {onRetry && (
        <button type="button" className="btn-o text-xs" onClick={onRetry}>
          إعادة المحاولة
        </button>
      )}
    </div>
  );
}

/** Wraps a query state: loading / error / content. */
export function QueryState<T>({ q, children }: { q: { isLoading: boolean; error: unknown; data?: T; refetch: () => unknown }; children: (d: T) => ReactNode }) {
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.data === undefined) return <Loading />;
  return <>{children(q.data)}</>;
}

export function Chip({
  active,
  onClick,
  children,
  dark,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  dark?: boolean;
  className?: string;
}) {
  const on = dark ? 'bg-[#5C1528] text-white border-[#5C1528]' : 'bg-[#8B2942] text-white border-[#8B2942]';
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn('rounded-full px-3 py-1.5 text-xs font-bold border', active ? on : 'bg-white text-[#a84d62] border-[#e0c8ce]', className)}
    >
      {children}
    </button>
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <nav className="mt-4 flex items-center justify-center gap-2 text-sm no-print" aria-label="ترقيم الصفحات">
      <button type="button" className="btn-o text-xs" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        السابق
      </button>
      <span className="text-[#a84d62]">
        صفحة {page} من {pages} · {total} سجل
      </span>
      <button type="button" className="btn-o text-xs" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        التالي
      </button>
    </nav>
  );
}

/** Field wrapper with the original's `label.lbl` styling, properly associated. */
export function Field({ label, children, className }: { label: ReactNode; children: (id: string) => ReactNode; className?: string }) {
  const id = useId();
  return (
    <div className={className}>
      <label className="lbl" htmlFor={id}>
        {label}
      </label>
      {children(id)}
    </div>
  );
}
